import useSession from '@/hooks/useSession';
import { dateDiffInDays, getToday, truncateText } from '@/utils/functions';
import {
  ChangeEvent,
  FormEvent,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './search.module.scss';
import SEOHelmet from '@/components/SEOHelmet';
import MsgParagraph from '@/components/MsgParagraph';
import Button from '@/components/Button';
import MagnifyingGlassIcon from '@/assets/icons/magnifying-glass.svg?react';

const SNIPPET_LENGTH = 200;

/* Shows the part of the text around the first match, so the match is visible */
const getSnippet = (text: string, query: string): string => {
  const index = query ? text.toLocaleLowerCase().indexOf(query) : -1;
  if (index < SNIPPET_LENGTH - query.length) {
    return truncateText(text, SNIPPET_LENGTH);
  }
  const start = Math.max(0, index - SNIPPET_LENGTH / 2);
  return `...${truncateText(text.substring(start), SNIPPET_LENGTH)}`;
};

/* Wraps each case-insensitive match of `query` in <mark> */
const highlight = (text: string, query: string): ReactNode => {
  if (!query) return text;
  const lower = text.toLocaleLowerCase();
  const parts: ReactNode[] = [];
  let from = 0;
  let index = lower.indexOf(query);
  while (index !== -1) {
    parts.push(text.substring(from, index));
    parts.push(
      <mark key={index}>{text.substring(index, index + query.length)}</mark>
    );
    from = index + query.length;
    index = lower.indexOf(query, from);
  }
  parts.push(text.substring(from));
  return parts;
};

const Search = () => {
  const {
    session: { threads },
  } = useSession();
  const [query, setQuery] = useState<string>('');
  const { pushUserEvent } = useSession();
  const navigate = useNavigate();
  const today = useRef(getToday());

  const filteredThreads = useMemo(() => {
    if (query === '') return threads;
    return threads.filter((thread) => {
      const titleMatched = thread.title.toLocaleLowerCase().includes(query);

      if (titleMatched) return true;

      const contentMatched = thread.items.some(
        (item) =>
          item.prompt.toLocaleLowerCase().includes(query) ||
          item.response.some((response) => {
            try {
              return response.text.toLocaleLowerCase().includes(query);
            } catch (e) {
              return false;
            }
          })
      );

      if (contentMatched) return true;

      return false;
    });
  }, [threads, query]);

  const onSubmit = (e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    handleSearch((formData.get('search') as string) || '');
  };

  const onSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  const handleSearch = (value: string): void => {
    setQuery(value.toLocaleLowerCase());
  };

  return (
    <div className={styles['search-wrapper']}>
      <SEOHelmet title="Search Threads" />
      <h1>Search threads</h1>

      {threads.length === 0 ? (
        <MsgParagraph>
          <br />
          <br />
          You don't have any threads yet. Start your first thread to begin
          exploring and searching with GANNET. <br />
          <br />
          <br />
          <Button
            onClick={() => {
              pushUserEvent('click_start_first_thread');
              navigate('/');
            }}
          >
            New Thread
          </Button>
        </MsgParagraph>
      ) : (
        <>
          <form
            className={styles['search-form']}
            onSubmit={onSubmit}
            role="search"
          >
            <MagnifyingGlassIcon aria-hidden="true" />
            <input
              type="search"
              id="search"
              name="search"
              placeholder="Type to search..."
              aria-label="Search threads"
              onChange={onSearchChange}
              required
            />
          </form>
          <p className={styles['search-count']} aria-live="polite">
            {query &&
              filteredThreads.length > 0 &&
              `${filteredThreads.length} ${
                filteredThreads.length > 1 ? 'threads match' : 'thread matches'
              } "${query}"`}
          </p>
          <ul className={styles['search-list']}>
            {filteredThreads.map((thread) => {
              const lastMessage = thread.items[thread.items.length - 1];
              const dateDiff = dateDiffInDays(
                new Date(lastMessage.queryDate),
                today.current
              );

              return (
                <li key={thread.id}>
                  <Link
                    to={`/thread/${thread.id}`}
                    onClick={() => {
                      pushUserEvent('click_search_result');
                    }}
                  >
                    {highlight(thread.title, query)}
                  </Link>

                  <p>
                    {lastMessage.response[0]?.text
                      ? highlight(
                          getSnippet(lastMessage.response[0].text, query),
                          query
                        )
                      : 'N/A'}
                  </p>
                  <span>
                    <strong>Last update:</strong>
                    {dateDiff > 0
                      ? `${dateDiff} day${dateDiff > 1 ? 's' : ''} ago`
                      : 'Today'}
                  </span>
                </li>
              );
            })}
            {filteredThreads.length === 0 && (
              <MsgParagraph>
                Couldn't find any mentions of <strong>"{query}"</strong> in your
                threads.
              </MsgParagraph>
            )}
          </ul>
        </>
      )}
    </div>
  );
};

export default Search;
